type RawLocation = {
  latitudeE7: number;
  longitudeE7: number;
  timestamp: string;
  accuracy?: number;
};

const toE7 = (value: number) => Math.round(value * 10_000_000);

function point(lat: number, lng: number, timestamp: string, accuracy = 35): RawLocation {
  return {
    latitudeE7: toE7(lat),
    longitudeE7: toE7(lng),
    timestamp,
    accuracy,
  };
}

function interpolate(
  start: { lat: number; lng: number },
  end: { lat: number; lng: number },
  startIso: string,
  minutes: number,
  count: number,
): RawLocation[] {
  return Array.from({ length: count }, (_, index) => {
    const ratio = count === 1 ? 0 : index / (count - 1);
    const date = new Date(Date.parse(startIso) + ratio * minutes * 60_000);
    return point(start.lat + (end.lat - start.lat) * ratio, start.lng + (end.lng - start.lng) * ratio, date.toISOString(), 45);
  });
}

function stay(center: { lat: number; lng: number }, startIso: string, minutes: number, samples: number): RawLocation[] {
  return Array.from({ length: samples }, (_, index) => {
    const date = new Date(Date.parse(startIso) + (minutes / Math.max(1, samples - 1)) * index * 60_000);
    const offset = (index % 3) * 0.00018;
    return point(center.lat + offset, center.lng - offset, date.toISOString(), 25);
  });
}

export function createTokyoDaySample(): unknown {
  const shinjuku = { lat: 35.6909, lng: 139.7003 };
  const shibuya = { lat: 35.6595, lng: 139.7005 };
  const roppongi = { lat: 35.6628, lng: 139.7315 };

  return {
    locations: [
      ...stay(shinjuku, '2026-04-18T00:00:00+09:00', 540, 8),
      ...interpolate(shinjuku, shibuya, '2026-04-18T09:00:00+09:00', 35, 6),
      ...stay(shibuya, '2026-04-18T09:40:00+09:00', 150, 6),
      ...interpolate(shibuya, roppongi, '2026-04-18T12:20:00+09:00', 30, 6),
      ...stay(roppongi, '2026-04-18T13:00:00+09:00', 180, 7),
      ...interpolate(roppongi, shinjuku, '2026-04-18T17:20:00+09:00', 45, 6),
      ...stay(shinjuku, '2026-04-18T18:20:00+09:00', 220, 6),
    ],
  };
}

export function createTokyoOsakaTripSample(): unknown {
  const tokyoHome = { lat: 35.6909, lng: 139.7003 };
  const tokyoStation = { lat: 35.6812, lng: 139.7671 };
  const shinOsaka = { lat: 34.7335, lng: 135.5001 };
  const umeda = { lat: 34.7055, lng: 135.4983 };
  const namba = { lat: 34.6643, lng: 135.5011 };
  const osakaCastle = { lat: 34.6873, lng: 135.5262 };

  return {
    locations: [
      ...stay(tokyoHome, '2026-04-10T00:00:00+09:00', 450, 8),
      ...interpolate(tokyoHome, tokyoStation, '2026-04-10T07:40:00+09:00', 35, 5),
      ...interpolate(tokyoStation, shinOsaka, '2026-04-10T08:30:00+09:00', 150, 20),
      ...stay(umeda, '2026-04-10T11:30:00+09:00', 300, 10),
      ...stay(namba, '2026-04-10T18:00:00+09:00', 180, 8),
      ...stay(umeda, '2026-04-10T23:00:00+09:00', 420, 8),
      ...interpolate(umeda, osakaCastle, '2026-04-11T10:00:00+09:00', 35, 5),
      ...stay(osakaCastle, '2026-04-11T10:40:00+09:00', 180, 8),
      ...interpolate(osakaCastle, namba, '2026-04-11T14:00:00+09:00', 35, 5),
      ...stay(namba, '2026-04-11T15:00:00+09:00', 240, 8),
      ...stay(umeda, '2026-04-11T23:00:00+09:00', 420, 8),
      ...interpolate(shinOsaka, tokyoStation, '2026-04-12T15:00:00+09:00', 150, 20),
      ...interpolate(tokyoStation, tokyoHome, '2026-04-12T17:40:00+09:00', 35, 5),
      ...stay(tokyoHome, '2026-04-12T18:30:00+09:00', 240, 6),
    ],
  };
}

export function createNoiseSample(): unknown {
  const sample = createTokyoDaySample() as { locations: RawLocation[] };
  return {
    locations: [
      ...sample.locations.slice(0, 10),
      point(0, 0, '2026-04-18T11:00:00+09:00'),
      point(48.8566, 2.3522, '2026-04-18T11:05:00+09:00'),
      ...sample.locations.slice(10),
    ],
  };
}

export function createEmptySample(): unknown {
  return { locations: [] };
}

export function createUnknownSample(): unknown {
  return { exportedBy: 'unknown-app', events: [{ title: 'not a location timeline' }] };
}

export function createDeviceTimelineArraySample(): unknown {
  return [
    {
      startTime: '2026-04-20T08:00:00.000+09:00',
      endTime: '2026-04-20T09:00:00.000+09:00',
      visit: {
        probability: '0.92',
        topCandidate: {
          placeLocation: 'geo:35.69090,139.70030',
          probability: '0.92',
          semanticType: 'TYPE_HOME',
          placeID: 'sample-home',
        },
        hierarchyLevel: '0',
      },
    },
    {
      startTime: '2026-04-20T09:00:00.000+09:00',
      endTime: '2026-04-20T09:35:00.000+09:00',
      activity: {
        start: 'geo:35.69090,139.70030',
        end: 'geo:35.65950,139.70050',
        distanceMeters: '3600',
        topCandidate: {
          type: 'IN_PASSENGER_VEHICLE',
          probability: '0.8',
        },
      },
    },
    {
      startTime: '2026-04-20T09:35:00.000+09:00',
      endTime: '2026-04-20T10:35:00.000+09:00',
      timelinePath: [
        {
          point: 'geo:35.65950,139.70050',
          durationMinutesOffsetFromStartTime: '0',
        },
        {
          point: 'geo:35.66000,139.70400',
          durationMinutesOffsetFromStartTime: '30',
        },
        {
          point: 'geo:35.66280,139.73150',
          durationMinutesOffsetFromStartTime: '60',
        },
      ],
    },
    {
      startTime: '2026-04-20T10:35:00.000+09:00',
      endTime: '2026-04-20T12:20:00.000+09:00',
      visit: {
        probability: '0.88',
        topCandidate: {
          placeLocation: 'geo:35.66280,139.73150',
          probability: '0.88',
          semanticType: 'TYPE_SEARCHED_ADDRESS',
          placeID: 'sample-office',
        },
        hierarchyLevel: '0',
      },
    },
  ];
}
